import React, { useMemo } from 'react';
import {
    Users,
    TrendingUp,
    Droplets,
    UserCheck,
    AlertTriangle,
    Award,
    Activity,
    Target
} from 'lucide-react';
import { calculateHealthScore, getUserSegment } from '../../utils/UserHealthScore';
import { predictChurnRisk } from '../../utils/ChurnPrediction';

/**
 * Dashboard Metrics Component
 * Displays key performance metrics and user statistics
 */
const DashboardMetrics = ({ users }) => {
    const metrics = useMemo(() => {
        if (!users || users.length === 0) {
            return {
                totalUsers: 0,
                activeUsers: 0,
                avgShakeQuantity: 0,
                topUpConversion: 0,
                referralGrowth: 0,
                healthScoreDistribution: { vip: 0, active: 0, atRisk: 0, inactive: 0, critical: 0 },
                criticalAlerts: 0
            };
        }

        // Calculate metrics
        const totalUsers = users.length;

        // Active users (have shake quantity > 0 or topped up in last 30 days)
        const activeUsers = users.filter(user => {
            const quantity = user.shakeQuantity || 0;
            const lastTopUp = user.lastTopUpDate || user.shakeTopUp?.[0]?.date;

            if (quantity > 0) return true;

            if (lastTopUp) {
                const date = lastTopUp.seconds
                    ? new Date(lastTopUp.seconds * 1000)
                    : new Date(lastTopUp);
                const daysSince = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
                return daysSince <= 30;
            }

            return false;
        }).length;

        // Removed shake-related metrics

        // Referral growth rate (users with referrals)
        const usersWithReferrals = users.filter(user =>
            user.referredBy || (user.referralCount && user.referralCount > 0)
        ).length;
        const referralGrowth = totalUsers > 0 ? Math.round((usersWithReferrals / totalUsers) * 100) : 0;

        // Health score distribution
        const healthScoreDistribution = { vip: 0, active: 0, atRisk: 0, inactive: 0, critical: 0 };
        users.forEach(user => {
            const healthScore = calculateHealthScore(user);
            const category = healthScore.category.toLowerCase().replace(' ', '');
            if (healthScoreDistribution.hasOwnProperty(category)) {
                healthScoreDistribution[category]++;
            }
        });

        // Critical alerts (high churn risk only)
        const criticalAlerts = users.filter(user => {
            const churnRisk = predictChurnRisk(user);
            return churnRisk.risk === 'critical';
        }).length;

        return {
            totalUsers,
            activeUsers,
            referralGrowth,
            healthScoreDistribution,
            criticalAlerts
        };
    }, [users]);

    return (
        <div className="space-y-6">
            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    title="Total Users"
                    value={metrics.totalUsers}
                    icon={Users}
                    color="blue"
                    trend={null}
                />
                <MetricCard
                    title="Active Users"
                    value={metrics.activeUsers}
                    subtitle={`${metrics.totalUsers > 0 ? Math.round((metrics.activeUsers / metrics.totalUsers) * 100) : 0}% of total`}
                    icon={UserCheck}
                    color="green"
                />

                <MetricCard
                    title="Critical Alerts"
                    value={metrics.criticalAlerts}
                    icon={AlertTriangle}
                    color="red"
                    alert={metrics.criticalAlerts > 0}
                />
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MetricCard
                    title="Referral Growth"
                    value={`${metrics.referralGrowth}%`}
                    icon={Award}
                    color="yellow"
                />
                <MetricCard
                    title="User Engagement"
                    value={`${Math.round((metrics.activeUsers / (metrics.totalUsers || 1)) * 100)}%`}
                    icon={Activity}
                    color="indigo"
                />
            </div>

            {/* Health Score Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Health Score Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <DistributionCard
                        label="VIP"
                        count={metrics.healthScoreDistribution.vip}
                        total={metrics.totalUsers}
                        color="purple"
                    />
                    <DistributionCard
                        label="Active"
                        count={metrics.healthScoreDistribution.active}
                        total={metrics.totalUsers}
                        color="green"
                    />
                    <DistributionCard
                        label="At Risk"
                        count={metrics.healthScoreDistribution.atRisk}
                        total={metrics.totalUsers}
                        color="yellow"
                    />
                    <DistributionCard
                        label="Inactive"
                        count={metrics.healthScoreDistribution.inactive}
                        total={metrics.totalUsers}
                        color="orange"
                    />
                    <DistributionCard
                        label="Critical"
                        count={metrics.healthScoreDistribution.critical}
                        total={metrics.totalUsers}
                        color="red"
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ title, value, subtitle, icon: Icon, color, alert, trend }) => {
    const colorClasses = {
        blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', text: 'text-blue-900' },
        green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', text: 'text-green-900' },
        cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'text-cyan-600', text: 'text-cyan-900' },
        red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', text: 'text-red-900' },
        purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', text: 'text-purple-900' },
        yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600', text: 'text-yellow-900' },
        indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', text: 'text-indigo-900' }
    };

    const classes = colorClasses[color];

    return (
        <div className={`${classes.bg} border ${classes.border} rounded-xl p-6 ${alert ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}>
            <div className="flex items-start justify-between mb-3">
                <div className={`p-3 bg-white rounded-lg ${classes.icon}`}>
                    <Icon className="w-6 h-6" />
                </div>
                {alert && (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-semibold rounded-full">
                        ALERT
                    </span>
                )}
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
            <p className={`text-3xl font-bold ${classes.text}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
            {trend && (
                <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                    <span>{Math.abs(trend)}% vs last month</span>
                </div>
            )}
        </div>
    );
};

/**
 * Distribution Card Component
 */
const DistributionCard = ({ label, count, total, color }) => {
    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

    const colorClasses = {
        purple: { bg: 'bg-purple-100', text: 'text-purple-700', bar: 'bg-purple-500' },
        green: { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' },
        yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500' },
        orange: { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-500' },
        red: { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' }
    };

    const classes = colorClasses[color];

    return (
        <div className={`${classes.bg} rounded-lg p-4`}>
            <p className={`text-xs font-medium ${classes.text} mb-2`}>{label}</p>
            <p className={`text-2xl font-bold ${classes.text}`}>{count}</p>
            <div className="mt-2 bg-white rounded-full h-2 overflow-hidden">
                <div
                    className={`h-full ${classes.bar} transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <p className="text-xs text-gray-600 mt-1">{percentage}%</p>
        </div>
    );
};

export default DashboardMetrics;
